import { type RecoilState, useRecoilCallback, useRecoilValue, waitForAll } from 'recoil';
import { selectedBlocksState, blockSettings, blockInfoGraphicType } from 'venn-state';
import { useMemo } from 'react';

type InputState<T> = {
  state: 'checked' | 'unchecked' | 'indeterminate';
  id: T;
};

export const useSingleInputSelection = <T>(
  inputIdsState: RecoilState<T[]>,
  blockInputState: (id: string) => RecoilState<T | undefined>,
  isInputDisabled?: (blockId: string) => boolean,
) => {
  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const inputIds = useRecoilValue(inputIdsState);
  const blockInputSelections = useRecoilValue(waitForAll(selectedBlocks.map((id) => blockInputState(id))));

  const blockSettingsForSelected = useRecoilValue(waitForAll(selectedBlocks.map((id) => blockSettings(id))));
  const blockGraphicTypesForSelected = useRecoilValue(waitForAll(selectedBlocks.map((id) => blockInfoGraphicType(id))));

  const inputsState = useMemo((): InputState<T>[] => {
    const usedInputs = new Set(blockInputSelections);
    return inputIds.map((id) => {
      if (!usedInputs.has(id)) {
        return {
          state: 'unchecked',
          id,
        };
      }
      const isChecked = blockInputSelections.every((rangeId) => rangeId === id);
      return {
        state: isChecked ? 'checked' : 'indeterminate',
        id,
      };
    });
  }, [blockInputSelections, inputIds]);

  const anyBlockHasInputDisabled = useMemo(() => {
    return selectedBlocks.some((blockId) => isInputDisabled?.(blockId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlocks, isInputDisabled, blockSettingsForSelected, blockGraphicTypesForSelected]);

  const allBlocksHaveInputDisabled = useMemo(() => {
    return selectedBlocks.every((blockId) => isInputDisabled?.(blockId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlocks, isInputDisabled, blockSettingsForSelected, blockGraphicTypesForSelected]);

  // - If all blocks have the feature disabled, show no selection
  // - If some blocks have the feature disabled, show the current selection but disable interaction
  // - If no blocks have the feature disabled, show normal selection
  const selectedInput = useMemo(() => {
    const checkedInput = inputsState.find((input) => input.state === 'checked')?.id ?? '';
    return allBlocksHaveInputDisabled ? '' : checkedInput;
  }, [inputsState, allBlocksHaveInputDisabled]);

  const onInputSelect = useRecoilCallback(
    ({ set }) =>
      (inputId: T | undefined) => {
        selectedBlocks.forEach((blockId) => {
          if (!isInputDisabled?.(blockId)) {
            set(blockInputState(blockId), inputId);
          }
        });
      },
    [selectedBlocks, blockInputState, isInputDisabled],
  );

  // Disable the interface when:
  // 1. No blocks are selected
  // 2. Any selected block has the feature disabled
  const disabled = selectedBlocks.length === 0 || anyBlockHasInputDisabled;

  return {
    inputsState,
    selectedInput,
    onInputSelect,
    disabled,
  };
};
